// ✅ src/pages/VehicleV2/DieselConsumptionReport_Print.jsx
// FULL PRINT PAGE (supports BOTH querystring + navigation state)
// Backend:
//   KM  -> GET  https://nissi-india.com/api/dieselConsumption/kmConsumption
//   HR  -> GET  https://nissi-india.com/api/dieselConsumption/hrConsumption
//   PDF -> GET  https://nissi-india.com/api/dieselConsumption/exportPdf
//
// Works with URL like:
// /vehicle/DieselConsumptionReport_Print?vehtype=O&branch=Select&vehRegNo=&month=2&year=2019&fromDate=&toDate=&vehicleMainType=Select&vehicleName=&vehicleCode=&mode=vehiclewise&type=km
//
// And also works with navigation state:
// openPrintPage("/vehicle/DieselConsumptionReport_Print", { type:"km", vehtype:"O", branch:"8", ... })

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { FaFilePdf } from "react-icons/fa6";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "https://nissi-india.com/api/dieselConsumption";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

const safeDate = (v) => {
  const s = toStr(v);
  return s || "";
};

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

export default function DieselConsumptionReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ Supports BOTH navigation state and querystring
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // ✅ Merge querystring + state (state overrides)
  const passed = useMemo(() => {
    const fromQs = {
      vehtype: qs.get("vehtype"),
      branch: qs.get("branch"),
      vehbasis: qs.get("vehbasis"),
      month: qs.get("month"),
      year: qs.get("year"),
      fromDate: qs.get("fromDate"),
      toDate: qs.get("toDate"),
      vehRegNo: qs.get("vehRegNo"),
      vehicleName: qs.get("vehicleName"),
      vehicleCode: qs.get("vehicleCode"),
      vehicleMainType: qs.get("vehicleMainType"),
      mode: qs.get("mode"),
      type: qs.get("type"), // km|hr
    };

    return { ...fromQs, ...(location.state || {}) };
  }, [qs, location.state]);

  // ✅ Map your type param: km/hr -> KM/HR
  const reportType = useMemo(() => {
    const t = toStr(passed?.Type ?? passed?.type ?? passed?.dieselType ?? passed?.reportType).toLowerCase();
    if (t === "km" || t === "k" || t === "kmwise") return "KM";
    return "HR";
  }, [passed]);

  // ✅ Build backend params (normalize names to what backend expects)
  const filters = useMemo(() => {
    const vehtype = toStr(passed?.dieseltype ?? passed?.vehtype ?? passed?.vehicleType ?? passed?.vehicleTypeText);

    const branch = toStr(passed?.dieselbranch ?? passed?.branch ?? passed?.branchid ?? passed?.vehiclebranch);

    const vehbasis = toStr(passed?.dieselbasis ?? passed?.vehbasis ?? passed?.basis);

    const month = toStr(passed?.dieselmonth ?? passed?.month);
    const year = toStr(passed?.dieselyear ?? passed?.year);

    const fromdate = safeDate(passed?.dieselfromdate ?? passed?.fromdate ?? passed?.fromDate);
    const todate = safeDate(passed?.dieseltodate ?? passed?.todate ?? passed?.toDate);

    const vehregno = toStr(passed?.dieselregno ?? passed?.vehregno ?? passed?.vehRegNo ?? passed?.regno);

    const vehcode = toStr(
      passed?.dieselcode ?? passed?.vehcode ?? passed?.vehicleCode ?? passed?.vehiclecode ?? passed?.fld_vehicle_code
    );

    const vehname = toStr(
      passed?.dieselname ?? passed?.vehname ?? passed?.vehicleName ?? passed?.vehiclename ?? passed?.fld_vehicle_name
    );

    const fld_vehiclemaintypeid = toStr(
      passed?.dieselVehicletype ??
        passed?.fld_vehiclemaintypeid ??
        passed?.vehicleMainType ??
        passed?.vehicleTypeId ??
        passed?.vehicletypeid
    );

    return {
      vehtype,
      branch: isSelect(branch) ? "" : branch,
      vehbasis: isSelect(vehbasis) ? "" : vehbasis,
      month,
      year,
      fromdate,
      todate,
      vehregno,
      vehcode,
      vehname,
      fld_vehiclemaintypeid: isSelect(fld_vehiclemaintypeid) ? "" : fld_vehiclemaintypeid,
      Type: reportType,
      mode: toStr(passed?.mode),

      // optional common fields
      loginuserid: Number(sessionStorage.getItem("loginuserid")) || Number(passed?.loginuserid) || 0,
      branchType: toStr(sessionStorage.getItem("branchtype") || passed?.branchType || passed?.branchtype),
    };
  }, [passed, reportType]);

  const endpoint = useMemo(() => (reportType === "KM" ? "kmConsumption" : "hrConsumption"), [reportType]);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      // guard
      const allEmpty =
        !filters.vehtype &&
        !filters.branch &&
        !filters.vehbasis &&
        !filters.month &&
        !filters.year &&
        !filters.fromdate &&
        !filters.todate &&
        !filters.vehregno &&
        !filters.vehcode &&
        !filters.vehname &&
        !filters.fld_vehiclemaintypeid;

      if (allEmpty) {
        throw new Error("Print params missing. Open print from Diesel Consumption Report page.");
      }

      const { data } = await axios.get(`${API_BASE}/${endpoint}`, { params: filters });
      if (!data?.success) throw new Error(data?.message || "Failed to load report");

      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    try {
      setExporting(true);
      const { data } = await axios.get(`${API_BASE}/exportPdf`, { params: filters });
      if (!data?.success) throw new Error(data?.message || "Failed to export PDF");

      if (data?.url) window.open(data.url, "_blank");
      else toast.success(`PDF created: ${data?.fileName || "Diesel Consumption Report"}`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "PDF export failed";
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- best-effort column getters ---------------- */
  const getRegNo = (r) => pick(r, ["vehregno", "fld_vehicle_regno", "vehicle_regno", "RegNo", "regno"], "-");
  const getVehicleCode = (r) => pick(r, ["vehcode", "fld_vehicle_code", "vehiclecode", "veh_code"], "-");
  const getVehicleName = (r) => pick(r, ["vehname", "fld_vehicle_name", "vehiclename", "veh_name"], "-");
  const getBranch = (r) => pick(r, ["branch", "BranchName", "branchname", "fld_branchname"], "-");
  const getVehType = (r) => pick(r, ["vehtype", "VehicleType", "vehicletype", "fld_vehicletypename"], "-");
  const getBasis = (r) => pick(r, ["vehbasis", "VehicleBasis", "basis", "fld_vehiclebasis"], "-");

  const getDate = (r) => pick(r, ["date", "EntryDate", "entrydate", "FillingDate", "fld_date"], "");
  const getKm = (r) => pick(r, ["km", "KM", "TotalKM", "total_km", "fld_km"], "");
  const getHr = (r) => pick(r, ["hr", "HR", "Hrs", "TotalHR", "total_hr", "fld_hr"], "");
  const getDieselQty = (r) => pick(r, ["diesel_qty", "DieselQty", "fld_dieselqty", "qty", "Quantity", "Diesel"], "");
  const getDieselRate = (r) => pick(r, ["diesel_rate", "DieselRate", "rate", "Rate", "fld_rate"], "");
  const getAmount = (r) => pick(r, ["amount", "Amount", "fld_amount", "total_amount", "Total", "NetAmount"], "");

  const totals = useMemo(() => {
    const last = rows?.[rows.length - 1];
    const tQty = pick(last, ["TotalDieselQty", "total_diesel_qty", "tot_diesel_qty", "TotQty"], "");
    const tAmt = pick(last, ["TotalAmount", "total_amount", "tot_amount", "TotAmount"], "");
    return { tQty, tAmt };
  }, [rows]);

  return (
    <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
      <HeaderBar />
      <VehicleLayoutWrapper>
        <div className="w-full bg-white">
          {/* ✅ PRINT CSS */}
          <style>{`
            @page { size: A4 portrait; margin: 10mm; }
            @media print {
              body { background: #fff !important; }
              .no-print { display: none !important; }
              .print-area { margin: 0 !important; padding: 0 !important; }
            }
          `}</style>

          {/* Toolbar */}
          <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="text-sm">
              {loading ? (
                "Loading..."
              ) : errMsg ? (
                <span className="text-red-600">{errMsg}</span>
              ) : (
                `Type: ${reportType} | Records: ${rows.length}`
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>

              <button
                onClick={() => window.print()}
                disabled={loading || !rows.length}
                className={`bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
              >
                <FaPrint /> Print
              </button>

              <button
                onClick={exportPdf}
                disabled={exporting || loading}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60"
              >
                <FaFilePdf /> {exporting ? "Exporting..." : "PDF"}
              </button>
            </div>
          </div>

          {/* ✅ PRINT TEMPLATE */}
          <div className="print-area mx-auto max-w-[820px] p-4 print:p-0">
            <div className="border-2 border-black p-3">
              <div className="border border-black">
                {/* Header */}
                <div className="border-b border-black p-2">
                  <div className="flex items-center gap-3">
                    <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                      <div className="text-[10px] font-bold text-center leading-3">
                        UNDER
                        <br />
                        CONSTRUCTION
                      </div>
                    </div>

                    <div className="flex-1 text-center leading-5">
                      <div className="font-bold text-lg">Nissi Infotech</div>
                      <div className="font-semibold text-sm">Nissi</div>
                      <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                      <div className="text-[11px] mt-1">
                        3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                      </div>
                      <div className="text-[11px]">
                        Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="border-b border-black text-center font-bold py-2 text-sm">
                  DIESEL CONSUMPTION REPORT ({reportType})
                </div>

                {/* Filter info */}
                <div className="border-b border-black p-2 text-[12px]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div>
                        <span className="font-semibold">Vehicle Type</span> : {filters.vehtype || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Branch</span> : {filters.branch || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Basis</span> : {filters.vehbasis || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Month / Year</span> : {filters.month || "-"} /{" "}
                        {filters.year || "-"}
                      </div>
                    </div>

                    <div className="text-right">
                      <div>
                        <span className="font-semibold">From</span> : {filters.fromdate || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">To</span> : {filters.todate || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Reg No</span> : {filters.vehregno || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Vehicle</span> : {filters.vehcode || "-"} / {filters.vehname || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="p-2">
                  <table className="w-full border border-black border-collapse text-[12px]">
                    <thead>
                      <tr>
                        <th className="border border-black px-2 py-1 w-[5%]">S.No</th>
                        <th className="border border-black px-2 py-1 w-[12%]">Reg No</th>
                        <th className="border border-black px-2 py-1 w-[10%]">Veh Code</th>
                        <th className="border border-black px-2 py-1 w-[16%]">Vehicle Name</th>
                        <th className="border border-black px-2 py-1 w-[12%]">Branch</th>
                        <th className="border border-black px-2 py-1 w-[9%]">Type</th>
                        <th className="border border-black px-2 py-1 w-[9%]">Basis</th>
                        <th className="border border-black px-2 py-1 w-[10%]">Date</th>
                        <th className="border border-black px-2 py-1 w-[7%]">KM</th>
                        <th className="border border-black px-2 py-1 w-[7%]">HR</th>
                        <th className="border border-black px-2 py-1 w-[7%]">Qty</th>
                        <th className="border border-black px-2 py-1 w-[7%]">Rate</th>
                        <th className="border border-black px-2 py-1 w-[9%]">Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {!loading && rows.length === 0 ? (
                        <tr>
                          <td className="border border-black px-2 py-2 text-center" colSpan={13}>
                            No records found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => (
                          <tr key={pick(r, ["id", "fld_id", "RowId"], `${idx}`)}>
                            <td className="border border-black px-2 py-1 text-center">{idx + 1}</td>
                            <td className="border border-black px-2 py-1">{getRegNo(r)}</td>
                            <td className="border border-black px-2 py-1">{getVehicleCode(r)}</td>
                            <td className="border border-black px-2 py-1">{getVehicleName(r)}</td>
                            <td className="border border-black px-2 py-1">{getBranch(r)}</td>
                            <td className="border border-black px-2 py-1">{getVehType(r)}</td>
                            <td className="border border-black px-2 py-1">{getBasis(r)}</td>
                            <td className="border border-black px-2 py-1">{getDate(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getKm(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getHr(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getDieselQty(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getDieselRate(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getAmount(r) || "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>

                    {rows.length > 0 && (totals.tQty || totals.tAmt) && (
                      <tfoot>
                        <tr>
                          <td className="border border-black px-2 py-1 font-semibold text-right" colSpan={10}>
                            Total
                          </td>
                          <td className="border border-black px-2 py-1 font-semibold text-right">{totals.tQty || ""}</td>
                          <td className="border border-black px-2 py-1 font-semibold text-right"></td>
                          <td className="border border-black px-2 py-1 font-semibold text-right">{totals.tAmt || ""}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                {/* Footer */}
                <div className="border-t border-black p-2">
                  <div className="border border-black p-2 text-[11px]">
                    Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                  </div>
                </div>
              </div>
            </div>

            {/* Debug */}
            <div className="no-print mt-4 text-xs text-gray-600">
              <details>
                <summary className="cursor-pointer">Debug: View query + state + filters</summary>
                <pre className="mt-2 p-3 border rounded bg-gray-50 overflow-auto">
                  {JSON.stringify(
                    {
                      url: location.pathname + location.search,
                      passed,
                      reportType,
                      endpoint,
                      filters,
                      fetchedRowsCount: rows.length,
                    },
                    null,
                    2
                  )}
                </pre>
              </details>
            </div>
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </VehicleLayoutWrapper>
    </div>
  );
}